import { useState, useRef, useCallback } from 'react';
import { photosAPI, Photo, UploadProgress } from '../api/client';
import './PhotoUploader.css';

interface PhotoUploaderProps {
  sessionId: string;
  onClose: () => void;
  onUploadComplete: (photos: Photo[]) => void;
  /** Optional: set to 'revision' to upload a single revision for an existing photo */
  mode?: 'upload' | 'revision';
  /** Required when mode is 'revision' - the ID of the photo being revised */
  photoId?: string;
  /** Optional title override for the modal */
  title?: string;
}

interface FilePreview {
  file: File;
  preview: string;
  status: 'pending' | 'uploading' | 'success' | 'error';
  error?: string;
}

/**
 * Format file size
 */
const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

/**
 * Photo Uploader Component
 *
 * Modal with drag-drop zone, file selection, and upload progress.
 * Supports two modes:
 * - 'upload' (default): Upload multiple new photos to a session
 * - 'revision': Upload a single revision for an existing photo
 */
const PhotoUploader = ({ sessionId, onClose, onUploadComplete, mode = 'upload', photoId, title }: PhotoUploaderProps) => {
  const [files, setFiles] = useState<FilePreview[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<UploadProgress | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const isRevisionMode = mode === 'revision';
  const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
  const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
  const MAX_FILES = isRevisionMode ? 1 : 50;

  /**
   * Validate a file
   */
  const validateFile = (file: File): string | null => {
    if (!ALLOWED_TYPES.includes(file.type)) {
      return 'Invalid file type. Only JPEG, PNG, and WebP are allowed.';
    }
    if (file.size > MAX_FILE_SIZE) {
      return `File too large. Maximum size is ${formatFileSize(MAX_FILE_SIZE)}.`;
    }
    return null;
  };

  /**
   * Process selected files
   */
  const processFiles = useCallback((selectedFiles: FileList | File[]) => {
    const fileArray = Array.from(selectedFiles);
    const remainingSlots = MAX_FILES - files.length;

    if (fileArray.length > remainingSlots) {
      setUploadError(`You can only upload ${MAX_FILES} photos per session. ${remainingSlots} slots remaining.`);
      return;
    }

    const newPreviews: FilePreview[] = [];

    for (const file of fileArray) {
      const error = validateFile(file);
      const preview = URL.createObjectURL(file);

      newPreviews.push({
        file,
        preview,
        status: error ? 'error' : 'pending',
        error: error || undefined,
      });
    }

    setFiles((prev) => [...prev, ...newPreviews]);
    setUploadError(null);
  }, [files.length]);

  /**
   * Handle file input change
   */
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      processFiles(e.target.files);
    }
    // Reset input so the same file can be selected again
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  /**
   * Handle drag events
   */
  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      processFiles(e.dataTransfer.files);
    }
  };

  /**
   * Remove a file from the list
   */
  const removeFile = (index: number) => {
    setFiles((prev) => {
      const newFiles = [...prev];
      URL.revokeObjectURL(newFiles[index].preview);
      newFiles.splice(index, 1);
      return newFiles;
    });
  };

  /**
   * Upload all pending files - one at a time for accurate progress tracking
   */
  const handleUpload = async () => {
    const pendingFiles = files.filter((f) => f.status === 'pending');
    if (pendingFiles.length === 0) return;

    setIsUploading(true);
    setUploadError(null);

    const totalFiles = pendingFiles.length;
    const allUploadedPhotos: Photo[] = [];

    try {
      // Handle revision mode differently
      if (isRevisionMode && photoId) {
        // Mark as uploading
        setFiles((prev) =>
          prev.map((f) =>
            f.status === 'pending' ? { ...f, status: 'uploading' as const } : f
          )
        );
        setUploadProgress({ loaded: 0, total: 1, percentage: 0 });

        // Upload single revision
        const file = pendingFiles[0].file;
        const result = await photosAPI.uploadVersion(sessionId, photoId, file);

        // Mark as success
        setFiles((prev) =>
          prev.map((f) =>
            f.status === 'uploading' ? { ...f, status: 'success' as const } : f
          )
        );
        setUploadProgress({ loaded: 1, total: 1, percentage: 100 });

        // Call completion callback with the updated photo
        onUploadComplete([result.photo]);
      } else {
        // Upload files one at a time for accurate progress
        for (let i = 0; i < pendingFiles.length; i++) {
          const filePreview = pendingFiles[i];
          const fileIndex = files.findIndex((f) => f.file === filePreview.file);

          // Mark this specific file as uploading
          setFiles((prev) =>
            prev.map((f, idx) =>
              idx === fileIndex ? { ...f, status: 'uploading' as const } : f
            )
          );

          // Update progress to show current file being processed
          setUploadProgress({
            loaded: i,
            total: totalFiles,
            percentage: Math.round((i / totalFiles) * 100),
          });

          try {
            // Upload single file
            const result = await photosAPI.upload(sessionId, [filePreview.file]);

            // Check if this file succeeded
            const failed = result.failed.find(
              (fail) => fail.filename === filePreview.file.name
            );

            if (failed) {
              setFiles((prev) =>
                prev.map((f, idx) =>
                  idx === fileIndex
                    ? { ...f, status: 'error' as const, error: failed.error }
                    : f
                )
              );
            } else {
              setFiles((prev) =>
                prev.map((f, idx) =>
                  idx === fileIndex ? { ...f, status: 'success' as const } : f
                )
              );
              allUploadedPhotos.push(...result.photos);
            }
          } catch (err) {
            console.error(`Upload failed for ${filePreview.file.name}:`, err);
            setFiles((prev) =>
              prev.map((f, idx) =>
                idx === fileIndex
                  ? { ...f, status: 'error' as const, error: 'Upload failed' }
                  : f
              )
            );
          }

          // Update progress after each file completes
          setUploadProgress({
            loaded: i + 1,
            total: totalFiles,
            percentage: Math.round(((i + 1) / totalFiles) * 100),
          });
        }

        // Call completion callback with all new photos
        if (allUploadedPhotos.length > 0) {
          onUploadComplete(allUploadedPhotos);
        }
      }
    } catch (err) {
      console.error('Upload failed:', err);
      setUploadError('Upload failed. Please try again.');
    } finally {
      setIsUploading(false);
      setUploadProgress(null);
    }
  };

  /**
   * Cleanup previews on unmount
   */
  // Note: In production, you'd use useEffect to cleanup on unmount

  const pendingCount = files.filter((f) => f.status === 'pending').length;
  const successCount = files.filter((f) => f.status === 'success').length;
  const errorCount = files.filter((f) => f.status === 'error').length;

  return (
    <div className="uploader-overlay" onClick={onClose}>
      <div className="uploader-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="uploader-header">
          <h2>{title || (isRevisionMode ? 'Upload Revision' : 'Upload Photos')}</h2>
          <button
            className="uploader-close"
            onClick={onClose}
            disabled={isUploading}
            aria-label="Close"
          >
            <svg viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
            </svg>
          </button>
        </div>

        {/* Drop zone */}
        <div
          className={`drop-zone ${isDragging ? 'dragging' : ''} ${files.length > 0 ? 'has-files' : ''}`}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept={ALLOWED_TYPES.join(',')}
            multiple
            onChange={handleFileChange}
            className="file-input"
            disabled={isUploading}
          />

          <div className="drop-zone-content">
            <div className="drop-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 16.5V9.75m0 0l3 3m-3-3l-3 3M6.75 19.5a4.5 4.5 0 01-1.41-8.775 5.25 5.25 0 0110.233-2.33 3 3 0 013.758 3.848A3.752 3.752 0 0118 19.5H6.75z" />
              </svg>
            </div>
            <p className="drop-text">
              <strong>{isRevisionMode ? 'Drop revised image here' : 'Drop photos here'}</strong> or click to browse
            </p>
            <p className="drop-hint">
              JPEG, PNG, or WebP up to {formatFileSize(MAX_FILE_SIZE)}{isRevisionMode ? '' : ' each'}
            </p>
          </div>
        </div>

        {/* Error message */}
        {uploadError && (
          <div className="upload-error" role="alert">
            <svg viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
            <span>{uploadError}</span>
          </div>
        )}

        {/* File list */}
        {files.length > 0 && (
          <div className="file-list">
            <div className="file-list-header">
              <span>{files.length} file{files.length !== 1 ? 's' : ''} selected</span>
              {successCount > 0 && (
                <span className="success-count">{successCount} uploaded</span>
              )}
              {errorCount > 0 && (
                <span className="error-count">{errorCount} failed</span>
              )}
            </div>

            <div className="file-previews">
              {files.map((file, index) => (
                <div
                  key={`${file.file.name}-${index}`}
                  className={`file-preview ${file.status}`}
                >
                  <img src={file.preview} alt={file.file.name} />
                  <div className="file-preview-info">
                    <span className="file-name" title={file.file.name}>
                      {file.file.name}
                    </span>
                    <span className="file-size">{formatFileSize(file.file.size)}</span>
                    {file.error && <span className="file-error">{file.error}</span>}
                  </div>

                  {file.status === 'pending' && !isUploading && (
                    <button
                      className="file-remove"
                      onClick={() => removeFile(index)}
                      aria-label={`Remove ${file.file.name}`}
                    >
                      <svg viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                    </button>
                  )}

                  {file.status === 'uploading' && (
                    <div className="file-uploading">
                      <div className="mini-spinner" />
                    </div>
                  )}

                  {file.status === 'success' && (
                    <div className="file-success">
                      <svg viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </div>
                  )}

                  {file.status === 'error' && (
                    <div className="file-failed">
                      <svg viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                      </svg>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Upload progress */}
        {isUploading && uploadProgress && (
          <div className="upload-progress">
            <div className="progress-bar">
              <div
                className="progress-fill"
                style={{ width: `${uploadProgress.percentage}%` }}
              />
            </div>
            <span className="progress-text">
              Uploading {uploadProgress.loaded} of {uploadProgress.total} photos... {uploadProgress.percentage}%
            </span>
          </div>
        )}

        {/* Actions */}
        <div className="uploader-actions">
          <button
            className="btn-cancel"
            onClick={onClose}
            disabled={isUploading}
          >
            {successCount > 0 ? 'Done' : 'Cancel'}
          </button>
          {pendingCount > 0 && (
            <button
              className="btn-upload"
              onClick={handleUpload}
              disabled={isUploading || pendingCount === 0}
            >
              {isUploading ? (
                <>
                  <span className="btn-spinner" />
                  Uploading...
                </>
              ) : (
                `Upload ${pendingCount} Photo${pendingCount !== 1 ? 's' : ''}`
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default PhotoUploader;
